import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";

import appCssInline from "../styles.css?inline";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SkipToContent } from "../components/site/SkipToContent";


const CookieNotice = lazy(() =>
  import("../components/site/CookieNotice").then((m) => ({ default: m.CookieNotice })),
);
const FloatingControls = lazy(() =>
  import("../components/site/FloatingControls").then((m) => ({ default: m.FloatingControls })),
);
const Analytics = lazy(() =>
  import("../components/site/Analytics").then((m) => ({ default: m.Analytics })),
);
function DeferredChrome() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
    }).requestIdleCallback;
    if (idle) {
      idle(() => setReady(true));
      return;
    }
    const t = window.setTimeout(() => setReady(true), 900);
    return () => window.clearTimeout(t);
  }, []);

  if (!ready) return null;

  return (
    <Suspense fallback={null}>
      <CookieNotice />
      <FloatingControls />
      <Analytics />
    </Suspense>
  );
}




function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "adaptED" },
      { name: "author", content: "adaptED" },
      { name: "robots", content: "index, follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "adaptED" },
      { property: "og:locale", content: "en_ZA" },
      {
        property: "og:image",
        content: "https://www.adapted.info/__l5e/assets-v1/ddf459f9-9bda-4f99-8b5a-5df16e601132/hero-3.png",
      },
      { property: "og:image:alt", content: "adaptED, South Africa's adaptive AI learning companion for Grades 10-12." },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:image",
        content: "https://www.adapted.info/__l5e/assets-v1/ddf459f9-9bda-4f99-8b5a-5df16e601132/hero-3.png",
      },
      { name: "twitter:image:alt", content: "adaptED, South Africa's adaptive AI learning companion for Grades 10-12." },
      {
        name: "google-site-verification",
        content: "WuoIwLdgdIqadJF8R_lnuCoU2nswP6jr7rcIH_c2YLw",
      },

    ],
    styles: [{ children: appCssInline }],
    links: [
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/archivo-latin.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/dm-sans-latin.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.adapted.info/#organization",
              name: "adaptED",
              url: "https://www.adapted.info/",
              description:
                "We're building South Africa's frontier AI-native and CAPS-native learning companion for Grades 10-12, with adaptive, personalised matric exam support.",
              areaServed: { "@type": "Country", name: "South Africa" },
              sameAs: [
                "https://www.tiktok.com/@adapted_sa",
                "https://www.linkedin.com/company/adapt-ed-rsa/",
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://www.adapted.info/#website",
              url: "https://www.adapted.info/",
              name: "adaptED",
              inLanguage: "en-ZA",
              publisher: { "@id": "https://www.adapted.info/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-ZA">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    // Auth client is loaded off the critical path: it is only needed once the
    // page is interactive, and it is one of the largest client chunks.
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    const start = () => {
      void import("@/integrations/supabase/client").then(({ supabase }) => {
        if (cancelled) return;
        const { data } = supabase.auth.onAuthStateChange((event) => {
          if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
          router.invalidate();
          if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
        });
        unsubscribe = () => data.subscription.unsubscribe();
      });
    };

    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
    }).requestIdleCallback;
    const timer = idle ? undefined : window.setTimeout(start, 1200);
    if (idle) idle(start);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      unsubscribe?.();
    };
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <SkipToContent />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <DeferredChrome />
    </QueryClientProvider>

  );
}
